'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function InvestmentCalculator() {
  const t = useTranslations("tools.investmentCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    startingAmount: 20000,
    contribution: 1000,
    contributionTiming: 'end' as 'beginning' | 'end',
    contributionFrequency: 'monthly' as 'monthly' | 'annually',
    years: 10,
    returnRate: 6,
    compoundFrequency: 12,
    targetAmount: 1000000,
  });

  const [currentMode, setCurrentMode] = useState<'end-amount' | 'starting-amount' | 'return-rate' | 'contribution' | 'invest-length'>('end-amount');
  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  useEffect(() => {
    calculateInvestment();
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const formatCurrency = (value: number) => {
    return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const calculateInvestment = () => {
    const { startingAmount, contribution, years, returnRate, compoundFrequency, contributionTiming, contributionFrequency } = inputs;

    const contributionsPerYear = contributionFrequency === 'monthly' ? 12 : 1;
    const ratePerPeriod = returnRate / 100 / compoundFrequency;
    const totalPeriods = years * compoundFrequency;

    let balance = startingAmount;
    let totalContributions = 0;
    let schedule = [];

    for (let year = 1; year <= years; year++) {
      const yearStartBalance = balance;
      let yearContributions = 0;
      let yearInterest = 0;

      for (let period = 1; period <= compoundFrequency; period++) {
        const periodContribution = (period % (compoundFrequency / contributionsPerYear) === (contributionTiming === 'beginning' ? 1 : 0)) ? contribution : 0;

        if (contributionTiming === 'beginning' && periodContribution > 0) {
          balance += periodContribution;
          yearContributions += periodContribution;
          totalContributions += periodContribution;
        }

        const interest = balance * ratePerPeriod;
        balance += interest;
        yearInterest += interest;

        if (contributionTiming === 'end' && periodContribution > 0) {
          balance += periodContribution;
          yearContributions += periodContribution;
          totalContributions += periodContribution;
        }
      }

      schedule.push({
        year: year,
        startBalance: yearStartBalance,
        contributions: yearContributions,
        interest: yearInterest,
        endBalance: balance
      });
    }

    const totalInterest = balance - startingAmount - totalContributions;

    setResults({
      endBalance: balance,
      startingAmount: startingAmount,
      totalContributions: totalContributions,
      totalInterest: totalInterest,
      schedule: schedule
    });
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    calculateInvestment();
  };

  const handleInputChange = (field: string, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const renderPieChart = () => {
    if (!results) return null;

    const total = results.endBalance;
    const segments = [
      { label: t('startingAmountLabel'), value: results.startingAmount, color: '#2563eb' },
      { label: t('totalContributionsLabel'), value: results.totalContributions, color: '#8bbc21' },
      { label: t('interestEarnedLabel'), value: results.totalInterest, color: '#f59e0b' }
    ];

    const cx = 100;
    const cy = 100;
    const radius = 80;
    let currentAngle = -90;

    const paths = segments.map((segment, index) => {
      const percentage = (segment.value / total) * 100;
      const angle = (segment.value / total) * 360;
      const endAngle = currentAngle + angle;

      const x1 = cx + radius * Math.cos((currentAngle * Math.PI) / 180);
      const y1 = cy + radius * Math.sin((currentAngle * Math.PI) / 180);
      const x2 = cx + radius * Math.cos((endAngle * Math.PI) / 180);
      const y2 = cy + radius * Math.sin((endAngle * Math.PI) / 180);

      const largeArc = angle > 180 ? 1 : 0;

      const pathData = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;

      currentAngle = endAngle;

      return (
        <path
          key={index}
          d={pathData}
          fill={segment.color}
          stroke="white"
          strokeWidth="2"
        />
      );
    });

    return (
      <div style={{ marginTop: '2rem', textAlign: 'center' }}>
        <svg width="200" height="200">
          {paths}
        </svg>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1.5rem' }}>
          {segments.map((segment, index) => {
            const percentage = (segment.value / total) * 100;
            return (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: '20px', height: '20px', borderRadius: '0.25rem', background: segment.color }} />
                <div style={{ color: '#475569', fontSize: '0.9rem' }}>
                  {segment.label}: {formatCurrency(segment.value)} ({percentage.toFixed(1)}%)
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = {
    maxWidth: '100%',
    margin: '0 auto',
    background: 'white',
    borderRadius: '1rem',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden' as const,
  };

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const,
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const tabsStyle = {
    display: 'flex',
    background: '#f8fafc',
    borderBottom: '2px solid #e2e8f0',
    overflowX: 'auto' as const,
    flexWrap: screenSize === 'mobile' ? 'wrap' as const : 'nowrap' as const,
  };

  const tabStyle = (isActive: boolean) => getResponsiveStyle(
    {
      flex: 1,
      minWidth: '120px',
      background: isActive ? 'white' : 'transparent',
      border: 'none',
      cursor: 'pointer',
      fontWeight: 600,
      color: isActive ? '#2563eb' : '#64748b',
      transition: 'all 0.3s ease',
      borderBottom: isActive ? '3px solid #2563eb' : '3px solid transparent',
    },
    { fontSize: '0.85rem', padding: '0.75rem' },
    { fontSize: '0.9rem', padding: '0.875rem', minWidth: '140px' },
    { fontSize: '0.9rem', padding: '1rem', minWidth: '150px' }
  );

  const calculatorContentStyle = getResponsiveStyle(
    { display: 'grid', gap: '2rem' },
    { gridTemplateColumns: '1fr', padding: '1.5rem' },
    { gridTemplateColumns: '1fr', padding: '2rem' },
    { gridTemplateColumns: '1fr 1fr', padding: '2rem' }
  );

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
  };

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#334155' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      background: 'white',
      width: '100%',
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '0.75rem', fontSize: '1rem' }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
      width: '100%',
    },
    { padding: '1rem 2rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderRadius: '0.75rem',
      border: '2px solid #e2e8f0',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '1rem',
    background: 'white',
    borderRadius: '0.5rem',
    marginBottom: '0.75rem',
    borderLeft: '4px solid #e2e8f0',
    transition: 'all 0.3s ease',
  };

  const resultItemHighlightStyle = {
    ...resultItemStyle,
    background: 'linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)',
    borderLeft: '4px solid #2563eb',
    fontWeight: 600,
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
    },
    { padding: '1.5rem', marginBottom: '1.5rem' },
    { padding: '2rem', marginBottom: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', marginBottom: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  const listStyle = {
    ...paragraphStyle,
    paddingLeft: '1.5rem',
  };

  const scheduleTableStyle = getResponsiveStyle(
    {
      width: '100%',
      marginTop: '2rem',
      borderCollapse: 'collapse' as const,
      background: 'white',
      borderRadius: '0.5rem',
      overflow: 'hidden' as const,
    },
    { fontSize: '0.85rem' },
    { fontSize: '0.9rem' },
    { fontSize: '1rem' }
  );

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>💰 {t('toolTitle')}</h2>
          </div>

          <div style={tabsStyle}>
            <button
              style={tabStyle(currentMode === 'end-amount')}
              onClick={() => setCurrentMode('end-amount')}
            >
              {t('tabEndAmount')}
            </button>
            <button
              style={tabStyle(currentMode === 'starting-amount')}
              onClick={() => setCurrentMode('starting-amount')}
            >
              {t('tabStartingAmount')}
            </button>
            <button
              style={tabStyle(currentMode === 'return-rate')}
              onClick={() => setCurrentMode('return-rate')}
            >
              {t('tabReturnRate')}
            </button>
            <button
              style={tabStyle(currentMode === 'contribution')}
              onClick={() => setCurrentMode('contribution')}
            >
              {t('tabContribution')}
            </button>
            <button
              style={tabStyle(currentMode === 'invest-length')}
              onClick={() => setCurrentMode('invest-length')}
            >
              {t('tabInvestLength')}
            </button>
          </div>

          <div style={calculatorContentStyle}>
            <form onSubmit={handleCalculate} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {currentMode !== 'starting-amount' && (
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('startingAmountLabel')}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={inputs.startingAmount}
                    onChange={(e) => handleInputChange('startingAmount', parseFloat(e.target.value) || 0)}
                    step="0.01"
                    min="0"
                  />
                </div>
              )}

              {currentMode !== 'contribution' && (
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('contributionLabel')}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={inputs.contribution}
                    onChange={(e) => handleInputChange('contribution', parseFloat(e.target.value) || 0)}
                    step="0.01"
                    min="0"
                  />
                </div>
              )}

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('contributeAtLabel')}</label>
                <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      checked={inputs.contributionTiming === 'beginning'}
                      onChange={() => handleInputChange('contributionTiming', 'beginning')}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>{t('beginningOption')}</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      checked={inputs.contributionTiming === 'end'}
                      onChange={() => handleInputChange('contributionTiming', 'end')}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>{t('endOption')}</span>
                  </label>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('ofEachLabel')}</label>
                <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      checked={inputs.contributionFrequency === 'monthly'}
                      onChange={() => handleInputChange('contributionFrequency', 'monthly')}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>{t('monthOption')}</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      checked={inputs.contributionFrequency === 'annually'}
                      onChange={() => handleInputChange('contributionFrequency', 'annually')}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>{t('yearOption')}</span>
                  </label>
                </div>
              </div>

              {currentMode !== 'invest-length' && (
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('yearsLabel')}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={inputs.years}
                    onChange={(e) => handleInputChange('years', parseInt(e.target.value) || 0)}
                    step="1"
                    min="1"
                    max="100"
                  />
                </div>
              )}

              {currentMode !== 'return-rate' && (
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('returnRateLabel')}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={inputs.returnRate}
                    onChange={(e) => handleInputChange('returnRate', parseFloat(e.target.value) || 0)}
                    step="0.01"
                    min="0"
                    max="100"
                  />
                </div>
              )}

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('compoundFrequencyLabel')}</label>
                <select
                  style={inputStyle}
                  value={inputs.compoundFrequency}
                  onChange={(e) => handleInputChange('compoundFrequency', parseInt(e.target.value))}
                >
                  <option value="1">{t('annually')}</option>
                  <option value="2">{t('semiannually')}</option>
                  <option value="4">{t('quarterly')}</option>
                  <option value="12">{t('monthly')}</option>
                  <option value="24">{t('semimonthly')}</option>
                  <option value="26">{t('biweekly')}</option>
                  <option value="52">{t('weekly')}</option>
                  <option value="365">{t('daily')}</option>
                </select>
              </div>

              {currentMode !== 'end-amount' && (
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('targetAmountLabel')}</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={inputs.targetAmount}
                    onChange={(e) => handleInputChange('targetAmount', parseFloat(e.target.value) || 0)}
                    step="0.01"
                    min="0"
                  />
                </div>
              )}

              <button type="submit" style={calculateButtonStyle}>
                {t('calculateButton')}
              </button>
            </form>

            {results && (
              <div style={resultsStyle}>
                <h3 style={h3Style}>{t('resultsTitle')}</h3>
                <div>
                  <div style={resultItemHighlightStyle}>
                    <span style={{ color: '#64748b', fontWeight: 500 }}>{t('endBalanceLabel')}</span>
                    <span style={{ color: '#2563eb', fontWeight: 700, fontSize: '1.3rem' }}>
                      {formatCurrency(results.endBalance)}
                    </span>
                  </div>
                  <div style={resultItemStyle}>
                    <span style={{ color: '#64748b', fontWeight: 500 }}>{t('startingAmountLabel')}</span>
                    <span style={{ color: '#1e293b', fontWeight: 700, fontSize: '1.1rem' }}>
                      {formatCurrency(results.startingAmount)}
                    </span>
                  </div>
                  <div style={resultItemStyle}>
                    <span style={{ color: '#64748b', fontWeight: 500 }}>{t('totalContributionsLabel')}</span>
                    <span style={{ color: '#1e293b', fontWeight: 700, fontSize: '1.1rem' }}>
                      {formatCurrency(results.totalContributions)}
                    </span>
                  </div>
                  <div style={resultItemStyle}>
                    <span style={{ color: '#64748b', fontWeight: 500 }}>{t('totalInterestLabel')}</span>
                    <span style={{ color: '#1e293b', fontWeight: 700, fontSize: '1.1rem' }}>
                      {formatCurrency(results.totalInterest)}
                    </span>
                  </div>
                </div>

                {renderPieChart()}
              </div>
            )}
          </div>
        </div>

        {results && (
          <div style={getResponsiveStyle({}, { padding: '1.5rem' }, { padding: '2rem' }, { padding: '2rem' })}>
            <h3 style={h3Style}>{t('scheduleTitle')}</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={scheduleTableStyle}>
                <thead>
                  <tr>
                    <th style={{ background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', color: 'white', padding: screenSize === 'mobile' ? '0.5rem' : '1rem', textAlign: 'left', fontWeight: 600 }}>{t('yearColumn')}</th>
                    <th style={{ background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', color: 'white', padding: screenSize === 'mobile' ? '0.5rem' : '1rem', textAlign: 'left', fontWeight: 600 }}>{t('startingBalanceColumn')}</th>
                    <th style={{ background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', color: 'white', padding: screenSize === 'mobile' ? '0.5rem' : '1rem', textAlign: 'left', fontWeight: 600 }}>{t('contributionsColumn')}</th>
                    <th style={{ background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', color: 'white', padding: screenSize === 'mobile' ? '0.5rem' : '1rem', textAlign: 'left', fontWeight: 600 }}>{t('interestColumn')}</th>
                    <th style={{ background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', color: 'white', padding: screenSize === 'mobile' ? '0.5rem' : '1rem', textAlign: 'left', fontWeight: 600 }}>{t('endingBalanceColumn')}</th>
                  </tr>
                </thead>
                <tbody>
                  {results.schedule.map((row: any, index: number) => (
                    <tr key={index} style={{ background: 'transparent', transition: 'background 0.2s' }}>
                      <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem 1rem', borderBottom: index === results.schedule.length - 1 ? 'none' : '1px solid #e2e8f0', color: '#334155' }}>{row.year}</td>
                      <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem 1rem', borderBottom: index === results.schedule.length - 1 ? 'none' : '1px solid #e2e8f0', color: '#334155' }}>{formatCurrency(row.startBalance)}</td>
                      <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem 1rem', borderBottom: index === results.schedule.length - 1 ? 'none' : '1px solid #e2e8f0', color: '#334155' }}>{formatCurrency(row.contributions)}</td>
                      <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem 1rem', borderBottom: index === results.schedule.length - 1 ? 'none' : '1px solid #e2e8f0', color: '#334155' }}>{formatCurrency(row.interest)}</td>
                      <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem 1rem', borderBottom: index === results.schedule.length - 1 ? 'none' : '1px solid #e2e8f0', color: '#334155' }}><strong>{formatCurrency(row.endBalance)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep5')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep6')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <h3 style={h3Style}>{t('insightsPowerOfCompounding')}</h3>
        <p style={paragraphStyle}>{t('insightsPowerOfCompoundingText')}</p>

        <h3 style={h3Style}>{t('insightsTimeMatters')}</h3>
        <p style={paragraphStyle}>{t('insightsTimeMattersText')}</p>

        <h3 style={h3Style}>{t('insightsRegularContributions')}</h3>
        <p style={paragraphStyle}>{t('insightsRegularContributionsText')}</p>

        <h3 style={h3Style}>{t('insightsInflationTaxes')}</h3>
        <p style={paragraphStyle}>{t('insightsInflationTaxesText')}</p>

        <h3 style={h3Style}>{t('insightsRealisticReturns')}</h3>
        <p style={paragraphStyle}>{t('insightsRealisticReturnsText')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={h3Style}>{t('detailsKeyInputs')}</h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('detailsStartingAmount')}</strong> {t('detailsStartingAmountText')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('detailsContributions')}</strong> {t('detailsContributionsText')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('detailsTimeHorizon')}</strong> {t('detailsTimeHorizonText')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('detailsReturnRate')}</strong> {t('detailsReturnRateText')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('detailsCompounding')}</strong> {t('detailsCompoundingText')}</li>
        </ul>

        <h3 style={h3Style}>{t('detailsUnderstanding')}</h3>
        <p style={paragraphStyle}>{t('detailsUnderstandingText')}</p>

        <h3 style={h3Style}>{t('detailsLimitations')}</h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('detailsLimitation1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('detailsLimitation2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('detailsLimitation3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('detailsLimitation4')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={h3Style}>{t('faqQuestion1')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer1')}</p>

        <h3 style={h3Style}>{t('faqQuestion2')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer2')}</p>

        <h3 style={h3Style}>{t('faqQuestion3')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer3')}</p>

        <h3 style={h3Style}>{t('faqQuestion4')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer4')}</p>

        <h3 style={h3Style}>{t('faqQuestion5')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer5')}</p>

        <h3 style={h3Style}>{t('faqQuestion6')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer6')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('reference1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('reference2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('reference3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('reference4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('reference5')}</li>
        </ul>
      </section>
    </div>
  );
}